import PageHeading from '@shared/ui/PageHeading';
import BalancesTable from '@widgets/tables/BalancesTable';
import { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBalanceFilters } from '@utils/drawer-hooks';
import { FilterButton, ThemeButtonIcon } from '@shared/ui/buttons';
import { useSelector, useDispatch } from 'react-redux';
import {
  resetBalancesFilters,
  setBalancesParams,
  setBalancesSearch,
} from '@app/store/slices/balancesSlice';
import { SearchTable } from '@shared/ui/forms/search';
import { useMobileOverlay } from '@utils/hooks/useMobileOverlay';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme } from '@utils/libs/theme';
import TableMenuBtn from '@widgets/tables/main/TableMenuBtn';
import { TopUpBalanceBtn } from '@widgets/buttons';
import { BasicSelectInput } from '@shared/ui/forms/inputs';
import { useForm } from 'react-hook-form';
import { RenderItemBalance } from '@shared/ui/forms/select-render';
import { ALL_BANKS } from '@utils/tables/params';

export default function BalancesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openBalanceFilters } = useBalanceFilters();
  const { filtersCount, search, params } = useSelector(
    (state) => state.balances,
  );
  const { transferBankWallets, totalBalance } = useSelector(
    (state) => state.bankWallets,
  );
  const { control } = useForm({
    values: { bank_id: params.bank_id ?? ALL_BANKS },
  });
  const mobileSearch = useMobileOverlay();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    dispatch(setBalancesParams({ page: 1 }));
  }, []);

  const handleSearchChange = useCallback(
    (nextSearch) => {
      dispatch(setBalancesSearch(nextSearch));
    },
    [dispatch],
  );

  const filterButtonElement = useMemo(() => {
    return (
      <FilterButton
        onClick={openBalanceFilters}
        filtersCount={filtersCount}
        resetFilters={resetBalancesFilters}
      />
    );
  }, [filtersCount, openBalanceFilters]);

  const searchTableElement = useMemo(() => {
    return (
      <SearchTable
        handleSearch={handleSearchChange}
        value={search}
        placeholder={t('balances_search_placeholder')}
      />
    );
  }, [handleSearchChange, search, t]);

  const bankElement = useMemo(() => {
    return (
      <BasicSelectInput
        control={control}
        name="bank_id"
        data={[
          ...transferBankWallets.filter(
            (wallet) => wallet.bank_public_name !== null,
          ),
          {
            bank_id: ALL_BANKS,
            bank_public_name: t('total_balance'),
            balance: totalBalance,
            currency: 'USD',
          },
        ]}
        labelKey="bank_public_name"
        valueKey="bank_id"
        Render={RenderItemBalance}
        cleanable={false}
        onChange={(value) => {
          dispatch(
            setBalancesParams({
              page: 1,
              bank_id: value === ALL_BANKS ? null : value,
            }),
          );
        }}
      />
    );
  }, [control, dispatch, transferBankWallets, totalBalance, t]);

  return (
    <section className="table-page main-section">
      {isMobile && mobileSearch.isOpen && (
        <div className="search-mobile-container">
          <div className="block-pd-x search-mobile">
            <ThemeButtonIcon icon="arrow-left" onClick={mobileSearch.close} />
            {searchTableElement}
          </div>
        </div>
      )}

      {!mobileSearch.isOpen && (
        <PageHeading title={t('Balance')}>
          {isMobile && (
            <>
              {filterButtonElement}
              <ThemeButtonIcon icon="search" onClick={mobileSearch.open} />
            </>
          )}
          {!isMobile && (
            <>
              {/* <ThemeButton
                appearance="secondary"
                startIcon="export"
                block={false}
              >
                Export
              </ThemeButton> */}
              {/* временно скрыто
              <ThemeButton
                appearance="secondary"
                startIcon="arrow-down"
                block={false}
                onClick={() => {}}
              >
                Withdraw
              </ThemeButton> */}
              <TopUpBalanceBtn />
            </>
          )}
          {isMobile && <TableMenuBtn menu="balances" />}
        </PageHeading>
      )}
      {isMobile && (
        <div className="block-pd-x select-balance-container">{bankElement}</div>
      )}
      <div className="block-pd-x table-page__box">
        {!isMobile && (
          <div className="table-filters">
            <div className="table-filters-row">
              {searchTableElement}
              {bankElement}
            </div>
            {filterButtonElement}
          </div>
        )}
        <div className="table-page__table">
          <BalancesTable />
        </div>
      </div>
    </section>
  );
}
